import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bell, CurrencyInr, HourglassMedium, ArrowRight, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { playBalanceChime, isBalanceChimeMuted, setBalanceChimeMuted } from "@/lib/balanceChime";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

/**
 * Detects the current "notification window":
 *   morning → 10:00–12:00 (11 AM ±1h)
 *   evening → 22:00–00:00 (11 PM ±1h)
 * Returns null when outside both windows.
 */
function currentWindow(now = new Date()) {
    const h = now.getHours();
    if (h >= 10 && h < 12) return "morning";
    if (h >= 22) return "evening";
    return null;
}

const todayKey = (now = new Date()) => now.toISOString().slice(0, 10);

/**
 * Global 11-AM / 11-PM in-app balance notification.
 *
 * Once per window per user (persisted in localStorage), shows an auto-popup
 * modal with the current balance snapshot:
 *   - Admin  → total outstanding across all parties + pending slips count
 *   - Customer → own party's balance + pending slip count
 * A "?force=1" dev flag forces the modal open for testing.
 */
export default function BalanceNotification() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [snap, setSnap] = useState(null);
    const [win, setWin] = useState(null);
    // v1.28.17 — Local chime mute toggle for the Super User balance nudge.
    const [chimeMuted, setChimeMuted] = useState(isBalanceChimeMuted());
    const toggleChimeMuted = () => {
        const next = !chimeMuted;
        setBalanceChimeMuted(next);
        setChimeMuted(next);
    };

    const storageKey = useCallback((windowName, dateStr) => {
        return `micm.balance_notif.${user?.id || "anon"}.${dateStr}.${windowName}`;
    }, [user?.id]);

    const showFor = useCallback(async (windowName) => {
        if (!user || user === false) return;
        try {
            const endpoint = user.is_super_user
                ? "/super/dashboard/company-outstanding"
                : user.role === "admin"
                    ? "/dashboard/admin"
                    : "/dashboard/customer";
            const { data } = await api.get(endpoint);
            const role = user.is_super_user ? "super_user" : user.role;
            setSnap({ role, data });
            setWin(windowName);
            setOpen(true);
            // v1.28.17 — Play a soft attention chime for Super Users so
            // the twice-daily nudge is noticed even if the phone is
            // face-down. Respects the local mute toggle in the dialog
            // header + `prefers-reduced-motion`.
            if (user.is_super_user) {
                try { playBalanceChime(); } catch { /* noop */ }
            }
        } catch (_e) {
            // Silent fail — this is a background nudge, not a blocking flow
        }
    }, [user]);

    // Poll every 60s to see if we enter a fresh window we haven't shown today
    useEffect(() => {
        if (!user || user === false) return;

        // Dev shortcut: ?balance-notif=1 in URL forces the modal open once
        try {
            const p = new URLSearchParams(window.location.search);
            if (p.get("balance-notif") === "1") {
                const w = currentWindow() || "morning";
                // Persist so subsequent auto-checks in this window don't re-open the modal
                try { localStorage.setItem(storageKey(w, todayKey()), new Date().toISOString()); } catch (_e) { /* ignore quota */ }
                showFor(w);
                return;
            }
        } catch (_e) { /* ignore */ }

        const check = () => {
            const now = new Date();
            const w = currentWindow(now);
            if (!w) return;
            const key = storageKey(w, todayKey(now));
            if (localStorage.getItem(key)) return;
            localStorage.setItem(key, new Date().toISOString());
            showFor(w);
        };

        // Fire immediately on mount, then every 60 seconds
        check();
        const t = setInterval(check, 60_000);
        return () => clearInterval(t);
    }, [user, showFor, storageKey]);

    const dismiss = () => setOpen(false);

    const goDetails = () => {
        setOpen(false);
        if (snap?.role === "super_user") navigate("/admin/super/companies");
        else if (snap?.role === "admin") navigate("/admin");
        else navigate("/portal/ledger");
    };

    if (!snap) return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent data-testid="balance-notif-loading" className="sm:max-w-md">
                <DialogHeader><DialogTitle>Loading…</DialogTitle></DialogHeader>
            </DialogContent>
        </Dialog>
    );

    const isSuperUser = snap.role === "super_user";
    const isAdmin = snap.role === "admin";
    const d = snap.data || {};
    // v1.28.16 — SU payload shape differs (per-company list).
    const balance = isSuperUser ? d.grand_outstanding : isAdmin ? d.total_outstanding : d.balance;
    const pending = isSuperUser ? d.grand_pending_uploads : d.pending_uploads;
    const suCompanies = isSuperUser ? (d.companies || []) : [];
    const partyName = !isAdmin && !isSuperUser ? d.party?.name : null;

    const title = win === "morning" ? "Good morning · 11 AM balance check" : "End of day · 11 PM balance check";

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent data-testid="balance-notif-dialog" className="sm:max-w-md overflow-hidden p-0">
                {/* Header strip */}
                <div className={`bg-gradient-to-br ${win === "morning" ? "from-amber-400 via-amber-500 to-orange-500" : "from-indigo-600 via-indigo-700 to-slate-800"} px-6 py-5 text-white`}>
                    <div className="flex items-center gap-3">
                        <div className="rounded-full bg-white/20 p-2">
                            <Bell size={22} weight="fill" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <DialogTitle className="text-white font-display text-lg">{title}</DialogTitle>
                            <DialogDescription className="mt-0.5 text-white/85 text-xs">
                                {(() => { const d = new Date(); const p = (n) => String(n).padStart(2, "0"); const wk = d.toLocaleDateString("en-IN", { weekday: "long" }); return `${wk} ${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`; })()}
                            </DialogDescription>
                        </div>
                        {isSuperUser && (
                            <button
                                type="button"
                                data-testid="balance-notif-chime-toggle"
                                onClick={toggleChimeMuted}
                                title={chimeMuted ? "Chime is muted — tap to unmute" : "Chime is on — tap to mute"}
                                className="shrink-0 rounded-full bg-white/15 p-1.5 text-white hover:bg-white/25"
                            >
                                {chimeMuted ? <SpeakerSlash size={16} weight="fill" /> : <SpeakerHigh size={16} weight="fill" />}
                            </button>
                        )}
                    </div>
                </div>

                <div className="space-y-4 px-6 py-5">
                    {!isAdmin && !isSuperUser && partyName && (
                        <div className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="balance-notif-party">
                            Balance for <span className="font-semibold text-slate-800">{partyName}</span>
                        </div>
                    )}

                    <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4">
                        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                            {isSuperUser ? "Grand outstanding · every company" : isAdmin ? "Total outstanding (all parties)" : "Your closing balance"}
                        </div>
                        <div className="mt-2 flex items-baseline gap-1">
                            <CurrencyInr size={22} weight="bold" className="text-slate-500" />
                            <div className={`font-display text-3xl font-bold tabular-nums ${(balance || 0) > 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid="balance-notif-amount">
                                {fmt(balance)}
                            </div>
                        </div>
                        {(balance || 0) > 0 ? (
                            <div className="mt-1 text-xs text-rose-700">
                                {isSuperUser ? "Total pending collection across all tenants." : isAdmin ? "Amount pending collection from parties." : "This amount is currently due to MD Impex."}
                            </div>
                        ) : (
                            <div className="mt-1 text-xs text-emerald-700">All clear · no outstanding.</div>
                        )}
                    </div>

                    {isSuperUser && suCompanies.length > 0 && (
                        <div className="max-h-[42vh] overflow-y-auto rounded-lg border border-slate-200 bg-white" data-testid="balance-notif-su-companies">
                            <div className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                Outstanding per company · {suCompanies.length}
                            </div>
                            <ul className="divide-y divide-slate-100">
                                {suCompanies.map((c) => {
                                    const out = Number(c.total_outstanding || 0);
                                    const cls = out > 0 ? "text-rose-700" : out < 0 ? "text-emerald-700" : "text-slate-500";
                                    return (
                                        <li key={c.company_id} data-testid={`balance-notif-su-row-${c.company_id}`} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate font-semibold text-slate-800">{c.name}</div>
                                                <div className="text-[10px] uppercase tracking-wider text-slate-400">
                                                    {c.parties_count} {c.parties_count === 1 ? "party" : "parties"}
                                                    {c.pending_uploads > 0 && <span className="ml-1.5 text-amber-700">· {c.pending_uploads} pending slip{c.pending_uploads > 1 ? "s" : ""}</span>}
                                                    {!c.active && <span className="ml-1.5 rounded-full bg-slate-200 px-1.5 py-[1px] text-[9px] font-bold text-slate-600">INACTIVE</span>}
                                                </div>
                                            </div>
                                            <div className={`shrink-0 font-mono text-sm font-bold tabular-nums ${cls}`}>
                                                {fmt(out)}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    {pending > 0 && (
                        <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/70 p-3" data-testid="balance-notif-pending">
                            <HourglassMedium size={20} weight="fill" className="shrink-0 text-amber-700" />
                            <div className="text-sm text-amber-900">
                                <div className="font-semibold">{pending} slip{pending > 1 ? "s" : ""} pending verification</div>
                                <div className="text-xs text-amber-800/80">
                                    {isSuperUser ? "Across every company · dive into any tenant to verify." : isAdmin ? "Verify these to reduce your outstanding." : "We'll credit them once verified."}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="border-t border-slate-100 bg-slate-50 px-6 py-3">
                    <Button data-testid="balance-notif-dismiss" variant="outline" onClick={dismiss}>Dismiss</Button>
                    <Button data-testid="balance-notif-details" onClick={goDetails} className="bg-slate-900 text-white hover:bg-slate-800">
                        View details <ArrowRight size={14} className="ml-1.5" />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
